import { MIN_PADDING, MAX_PACKET_SIZE } from './constants.js';

/**
 * Cipher interface for packet encryption/decryption.
 * Implemented by aes128-gcm and future cipher modules.
 */
export type PacketCipher = {
	/** Encrypt a packet payload (padding_length + payload + padding) and return ciphertext + auth tag. */
	encrypt(packetLength: number, payload: Uint8Array, sequenceNumber: number): Promise<Uint8Array>;

	/** Decrypt ciphertext + auth tag back to (padding_length + payload + padding). */
	decrypt(
		packetLength: number,
		ciphertext: Uint8Array,
		sequenceNumber: number,
	): Promise<Uint8Array>;

	/** The block size used for padding alignment. */
	blockSize: number;
};

/**
 * Builds an SSH binary packet from a payload.
 *
 * Packet format (RFC 4253 §6):
 *   uint32    packet_length
 *   byte      padding_length
 *   byte[n1]  payload
 *   byte[n2]  random padding
 *
 * The total (packet_length + 4) must be a multiple of max(blockSize, 8).
 */
export function buildPacket(payload: Uint8Array, blockSize: number = 8): Uint8Array {
	const alignTo = Math.max(blockSize, 8);

	// packet_length = 1 (padding_length field) + payload + padding
	// total = 4 (packet_length field) + packet_length
	// total must be multiple of alignTo
	// padding must be >= MIN_PADDING

	const unpadded = 4 + 1 + payload.length;
	let paddingLength = alignTo - (unpadded % alignTo);
	if (paddingLength < MIN_PADDING) {
		paddingLength += alignTo;
	}

	const packetLength = 1 + payload.length + paddingLength;
	const total = 4 + packetLength;

	const packet = new Uint8Array(total);
	const view = new DataView(packet.buffer);

	// packet_length (uint32 big-endian)
	view.setUint32(0, packetLength);

	// padding_length (byte)
	packet[4] = paddingLength;

	// payload
	packet.set(payload, 5);

	// random padding
	const padding = new Uint8Array(paddingLength);
	crypto.getRandomValues(padding);
	packet.set(padding, 5 + payload.length);

	return packet;
}

/**
 * Accumulates bytes from the transport and extracts complete SSH packets.
 *
 * In plaintext mode, reads the 4-byte length prefix to determine packet boundaries.
 * When a cipher is set, delegates length reading and decryption to the cipher.
 */
export class PacketReader {
	private buffer = new Uint8Array(0);
	private cipher: PacketCipher | null = null;

	/**
	 * Set the cipher for decrypting incoming packets.
	 * Call after SSH_MSG_NEWKEYS is received.
	 */
	setCipher(cipher: PacketCipher): void {
		this.cipher = cipher;
	}

	/**
	 * Append incoming bytes to the internal buffer.
	 */
	append(data: Uint8Array): void {
		if (this.buffer.length === 0) {
			this.buffer = new Uint8Array(data);
			return;
		}
		const combined = new Uint8Array(this.buffer.length + data.length);
		combined.set(this.buffer);
		combined.set(data, this.buffer.length);
		this.buffer = combined;
	}

	/**
	 * Try to extract the next complete packet from the buffer.
	 * Returns the payload (without packet_length, padding_length, or padding),
	 * or null if not enough data is available yet.
	 *
	 * @param sequenceNumber The current receive sequence number (needed for GCM decryption).
	 */
	async nextPacket(sequenceNumber: number): Promise<Uint8Array | null> {
		if (this.cipher) {
			return this.nextPacketEncrypted(sequenceNumber);
		}
		return this.nextPacketPlaintext();
	}

	private nextPacketPlaintext(): Uint8Array | null {
		// need at least 4 bytes for packet_length
		if (this.buffer.length < 4) return null;

		const view = new DataView(this.buffer.buffer, this.buffer.byteOffset, this.buffer.byteLength);
		const packetLength = view.getUint32(0);

		if (packetLength > MAX_PACKET_SIZE) {
			throw new Error(`SSH packet too large: ${packetLength} bytes`);
		}

		const totalLength = 4 + packetLength;
		if (this.buffer.length < totalLength) return null;

		const paddingLength = this.buffer[4]!;
		const payloadLength = packetLength - 1 - paddingLength;

		if (payloadLength < 0) {
			throw new Error('SSH packet has invalid padding length');
		}

		// extract payload
		const payload = this.buffer.slice(5, 5 + payloadLength);

		// consume the packet from the buffer
		this.buffer = this.buffer.subarray(totalLength);

		return payload;
	}

	private async nextPacketEncrypted(sequenceNumber: number): Promise<Uint8Array | null> {
		const cipher = this.cipher!;

		// for GCM: packet_length (4 bytes) is unencrypted, followed by encrypted content + 16-byte tag
		// need at least 4 bytes for packet_length
		if (this.buffer.length < 4) return null;

		const view = new DataView(this.buffer.buffer, this.buffer.byteOffset, this.buffer.byteLength);
		const packetLength = view.getUint32(0);

		if (packetLength > MAX_PACKET_SIZE) {
			throw new Error(`SSH packet too large: ${packetLength} bytes`);
		}

		// encrypted content = packetLength bytes, plus 16-byte GCM auth tag
		const tagLength = 16;
		const totalLength = 4 + packetLength + tagLength;
		if (this.buffer.length < totalLength) return null;

		// extract the ciphertext + tag (everything after the 4-byte length)
		const ciphertext = this.buffer.slice(4, totalLength);

		// decrypt
		const plaintext = await cipher.decrypt(packetLength, ciphertext, sequenceNumber);

		// plaintext is: padding_length (1 byte) + payload + padding
		const paddingLength = plaintext[0]!;
		const payloadLength = packetLength - 1 - paddingLength;

		if (payloadLength < 0) {
			throw new Error('SSH packet has invalid padding length after decryption');
		}

		const payload = plaintext.slice(1, 1 + payloadLength);

		// consume
		this.buffer = this.buffer.subarray(totalLength);

		return payload;
	}
}

/**
 * Wraps a payload into a wire-ready packet, optionally encrypting it.
 *
 * @returns The complete wire bytes (ready to send on the transport).
 */
export async function wrapPacket(
	payload: Uint8Array,
	sequenceNumber: number,
	cipher: PacketCipher | null,
): Promise<Uint8Array> {
	if (!cipher) {
		return buildPacket(payload);
	}

	const blockSize = cipher.blockSize;
	const alignTo = Math.max(blockSize, 8);

	// for GCM, the 4-byte packet_length is AAD (not encrypted), so alignment
	// applies to the encrypted content only: padding_length + payload + padding.
	// content length = 1 + payload.length + paddingLength = packetLength,
	// which must be a multiple of the block size (RFC 5647 §7.1).
	const unpadded = 1 + payload.length;
	let paddingLength = alignTo - (unpadded % alignTo);
	if (paddingLength < MIN_PADDING) {
		paddingLength += alignTo;
	}

	const packetLength = 1 + payload.length + paddingLength;

	// build the plaintext content: padding_length + payload + padding
	const content = new Uint8Array(1 + payload.length + paddingLength);
	content[0] = paddingLength;
	content.set(payload, 1);
	const padding = new Uint8Array(paddingLength);
	crypto.getRandomValues(padding);
	content.set(padding, 1 + payload.length);

	// encrypt
	const encrypted = await cipher.encrypt(packetLength, content, sequenceNumber);

	// wire format for GCM: packet_length (unencrypted) + encrypted + tag
	const wire = new Uint8Array(4 + encrypted.length);
	new DataView(wire.buffer).setUint32(0, packetLength);
	wire.set(encrypted, 4);

	return wire;
}
